import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TrendUp, Users, Package, ShieldWarning, Sparkle, Plus, X, Copy, PaperPlaneTilt } from "@phosphor-icons/react";

const STAGES = [
  { key: "cold", label: "Cold", color: "bg-slate-200 text-slate-800" },
  { key: "toured", label: "Toured", color: "bg-cyan-200 text-cyan-900" },
  { key: "proposal_sent", label: "Proposal Sent", color: "bg-amber-200 text-amber-900" },
  { key: "signed", label: "Signed", color: "bg-emerald-200 text-emerald-900" },
  { key: "lost", label: "Lost", color: "bg-red-200 text-red-900" },
];

export default function BusinessDev() {
  const [tab, setTab] = useState("prospects");
  return (
    <AdminShell>
      <div className="space-y-4" data-testid="bd-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <TrendUp weight="fill" className="text-emerald-500"/> Business Development
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Grow the portfolio + surface upsell opportunities on existing landlords + spot churn risks.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 border-b-4 border-slate-900">
          {[
            { key: "prospects", label: "Prospects" },
            { key: "upsell", label: "Upsell Signals" },
            { key: "retention", label: "Retention Scores" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`bd-tab-${t.key}`}
              className={`h-10 px-4 font-black uppercase tracking-widest text-[11px] border-2 border-slate-900 -mb-1 ${
                tab === t.key ? "bg-slate-900 text-white shadow-[2px_2px_0_#0f172a]" : "bg-white text-slate-900 hover:bg-slate-100"
              }`}>{t.label}</button>
          ))}
        </div>

        {tab === "prospects" && <ProspectsTab/>}
        {tab === "upsell" && <UpsellTab/>}
        {tab === "retention" && <RetentionTab/>}
      </div>
    </AdminShell>
  );
}

function ProspectsTab() {
  const [prospects, setProspects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/business-dev/prospects"); setProspects(r.data || []); }
    catch { toast.error("Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const moveStage = async (p, newStage) => {
    try {
      await api.patch(`/business-dev/prospects/${p.id}`, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch (e) { toast.error("Move failed"); }
  };

  const money = (c) => c ? `$${(c/100).toLocaleString()}` : "—";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{prospects.length} prospects total</div>
        <button onClick={() => setShowNew(true)} data-testid="new-prospect-btn"
          className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[2px_2px_0_#0f172a] inline-flex items-center gap-1">
          <Plus size={12}/> New Prospect
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {prospects.map(p => {
          const stage = STAGES.find(s => s.key === p.stage) || STAGES[0];
          return (
            <div key={p.id} className="rugged-card p-3 space-y-2" data-testid={`prospect-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-black text-base truncate">{p.name}</div>
                  {p.company && <div className="text-xs text-slate-600 truncate">{p.company}</div>}
                  <div className="text-[10px] text-slate-500 mt-1">
                    {p.door_count > 0 && <>{p.door_count} doors · </>}
                    {p.est_monthly_value_cents > 0 && <>{money(p.est_monthly_value_cents)}/mo est.</>}
                  </div>
                </div>
                <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${stage.color}`}>{stage.label}</span>
              </div>
              {p.referral_from_landlord_name && (
                <div className="text-[10px] text-fuchsia-700">↪ Referred by {p.referral_from_landlord_name}</div>
              )}
              {p.notes && <div className="text-[11px] text-slate-700 line-clamp-2">{p.notes}</div>}
              {p.next_action && (
                <div className="p-1.5 bg-amber-50 border-l-4 border-amber-500 text-[10px]">
                  <b>Next:</b> {p.next_action} {p.next_action_at && `(${p.next_action_at})`}
                </div>
              )}
              <div className="flex flex-wrap gap-1 pt-1 border-t border-slate-100">
                <button onClick={() => setDrafting(p)} data-testid={`pitch-${p.id}`}
                  className="h-8 px-2 bg-emerald-600 text-white font-black uppercase tracking-widest text-[10px] shadow-[2px_2px_0_#0f172a] inline-flex items-center gap-1">
                  <Sparkle weight="fill" size={10}/> AI Pitch
                </button>
                {STAGES.map(s => (
                  s.key !== p.stage && (
                    <button key={s.key} onClick={() => moveStage(p, s.key)} data-testid={`stage-${p.id}-${s.key}`}
                      className={`h-8 px-2 border-2 border-slate-300 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px]`}>
                      → {s.label}
                    </button>
                  )
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showNew && <ProspectModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
      {drafting && <PitchModal prospect={drafting} onClose={() => setDrafting(null)}/>}
    </div>
  );
}

function ProspectModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", door_count: 0, stage: "cold",
    est_monthly_value_cents: 0, notes: "", source: "", next_action: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/business-dev/prospects", {
        ...form,
        door_count: parseInt(form.door_count) || 0,
        est_monthly_value_cents: Math.round((parseFloat(form.est_monthly_value_cents) || 0) * 100),
      });
      toast.success("Prospect added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="new-prospect-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">New Prospect</div>
          <button onClick={onClose} className="p-1 hover:bg-emerald-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Contact name" data-testid="prospect-name"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Company"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={form.door_count} onChange={e => setForm({...form, door_count: e.target.value})} placeholder="Doors"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input type="number" value={form.est_monthly_value_cents} onChange={e => setForm({...form, est_monthly_value_cents: e.target.value})} placeholder="Est monthly ($)"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !form.name} data-testid="prospect-save"
            className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Add Prospect"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PitchModal({ prospect, onClose }) {
  const [angle, setAngle] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const draft = async () => {
    setBusy(true);
    try {
      const r = await api.post("/business-dev/draft-pitch", { prospect_id: prospect.id, angle }, { timeout: 60000 });
      setResult(r.data);
    } catch (e) { toast.error("Draft failed"); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(`Subject: ${result.subject}\n\n${result.body}`);
    toast.success("Copied");
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="pitch-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">AI Pitch</div>
            <div className="font-black uppercase tracking-widest">{prospect.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-emerald-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          <textarea value={angle} onChange={e => setAngle(e.target.value)} rows={3} data-testid="pitch-angle"
            placeholder="Optional: specific angle / pain point ('they just fired their old PM over slow response times')"
            className="w-full p-3 border-2 border-slate-300 text-sm"/>
          <button onClick={draft} disabled={busy} data-testid="pitch-draft"
            className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Claude drafting…" : "Draft Pitch"}
          </button>
          {result && (
            <div className="p-3 border-4 border-slate-900 bg-slate-50 space-y-2" data-testid="pitch-result">
              <div className="font-black text-sm">{result.subject}</div>
              <pre className="text-xs whitespace-pre-wrap font-mono max-h-72 overflow-y-auto">{result.body}</pre>
              <button onClick={copy} data-testid="pitch-copy"
                className="w-full h-10 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-1">
                <Copy size={12}/> Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UpsellTab() {
  const [aged, setAged] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [pm, setPm] = useState(null);
  useEffect(() => {
    api.get("/business-dev/aged-appliances?min_years=12").then(r => setAged(r.data)).catch(() => {});
    api.get("/business-dev/coverage-gaps").then(r => setGaps(r.data)).catch(() => {});
    api.get("/business-dev/pm-coverage-gaps").then(r => setPm(r.data)).catch(() => {});
  }, []);

  const money = (c) => c ? `$${(c/100).toLocaleString()}` : "";

  return (
    <div className="space-y-4">
      <div className="rugged-card p-4" data-testid="aged-appliances">
        <div className="flex items-center gap-2 mb-3">
          <Package size={20} className="text-amber-600"/>
          <h3 className="font-black uppercase tracking-widest">Aged Appliances (12+ yrs)</h3>
          <span className="ml-auto text-2xl font-black text-amber-700">{aged?.count || 0}</span>
        </div>
        <div className="text-[11px] text-slate-600 mb-2">Ripe for replacement quotes to the owner.</div>
        <div className="max-h-56 overflow-y-auto space-y-1 text-xs">
          {(aged?.assets || []).slice(0, 15).map(a => (
            <div key={a.asset_id} className="flex items-center justify-between border-b border-slate-100 py-1">
              <div><b>{a.type}</b> · {a.make} · {a.address} unit {a.unit}</div>
              <div className="text-amber-700 font-black">{a.age_years}yr old</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rugged-card p-4" data-testid="coverage-gaps">
        <div className="flex items-center gap-2 mb-3">
          <ShieldWarning size={20} className="text-red-600"/>
          <h3 className="font-black uppercase tracking-widest">Insurance / Warranty Coverage Gaps</h3>
          <span className="ml-auto text-2xl font-black text-red-700">{gaps?.count || 0}</span>
        </div>
        <div className="text-[11px] text-slate-600 mb-2">Landlords without any active insurance/warranty policy — upsell AWR / home warranty.</div>
        <div className="max-h-56 overflow-y-auto space-y-1 text-xs">
          {(gaps?.landlords || []).map(g => (
            <div key={g.landlord_id} className="flex items-center justify-between border-b border-slate-100 py-1">
              <div><b>{g.name}</b> {g.principal_name && `· ${g.principal_name}`}</div>
              <div className="text-red-700 font-black">{g.property_count} doors uncovered</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rugged-card p-4" data-testid="pm-gaps">
        <div className="flex items-center gap-2 mb-3">
          <Users size={20} className="text-fuchsia-600"/>
          <h3 className="font-black uppercase tracking-widest">Properties Without PM Schedules</h3>
          <span className="ml-auto text-2xl font-black text-fuchsia-700">{pm?.count || 0}</span>
        </div>
        <div className="text-[11px] text-slate-600 mb-2">Preventive maintenance = recurring revenue.</div>
        <div className="max-h-56 overflow-y-auto space-y-1 text-xs">
          {(pm?.properties || []).slice(0, 15).map(p => (
            <div key={p.address_key} className="flex items-center justify-between border-b border-slate-100 py-1">
              <div><b>{p.address}</b></div>
              <div className="text-fuchsia-700 text-[10px]">{p.landlord_name || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RetentionTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/business-dev/retention-scores").then(r => setData(r.data)).catch(() => toast.error("Failed"));
  }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const buckets = { at_risk: [], watch: [], healthy: [] };
  data.landlords.forEach(l => buckets[l.bucket].push(l));
  const barColor = { at_risk: "bg-red-500", watch: "bg-amber-500", healthy: "bg-emerald-500" };
  return (
    <div className="space-y-4" data-testid="retention-list">
      {Object.entries(buckets).map(([bucket, list]) => (
        <div key={bucket} className="rugged-card p-4">
          <h3 className="font-black uppercase tracking-widest text-sm mb-2">
            {bucket.replace("_", " ")} · {list.length}
          </h3>
          <div className="space-y-2">
            {list.map(l => (
              <div key={l.landlord_id} className="p-2 border-2 border-slate-200 space-y-1" data-testid={`retention-${l.landlord_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-black text-sm">{l.name} {l.principal_name && <span className="text-[10px] text-slate-500">· {l.principal_name}</span>}</div>
                  <div className="font-black text-2xl">{l.score}</div>
                </div>
                <div className="h-2 bg-slate-200 overflow-hidden">
                  <div className={`h-full ${barColor[l.bucket]}`} style={{ width: `${l.score}%` }}/>
                </div>
                <div className="text-[10px] text-slate-600">{l.property_count} doors · {l.reasons.join(" · ")}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
